<template>
  <div id="job-search" class="fill-height" style="background-color: #f5f5f5;">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="5">
          <v-card class="elevation-2 rounded-xl">
            <v-card-title class="py-6 justify-center teal darken-4 rounded-t-xl">
              <h2 class="text-h5 font-weight-bold mb-0 white--text text-center">TRA CỨU KẾT QUẢ TUYỂN DỤNG</h2>
            </v-card-title>
            <v-card-text class="pa-6">
              <p class="text-center text-body-1 grey--text text--darken-2 mb-6">
                Vui lòng nhập số điện thoại hoặc CCCD/CMND để tra cứu trạng thái hồ sơ của bạn.
              </p>
              <v-form ref="form" v-model="valid" lazy-validation @submit.prevent>
                <v-text-field v-model="searchQuery" :rules="[v => !!v || 'Vui lòng nhập thông tin tra cứu']"
                  label="Số điện thoại / CCCD" outlined dense clearable prepend-inner-icon="mdi-magnify"
                  class="rounded-lg mb-4" hide-details="auto" @keyup.enter="search"></v-text-field>
              </v-form>

              <v-expand-transition>
                <div v-if="hasSearched && !isLoading" class="mt-6">
                  <!-- Placeholder cho kết quả tra cứu -->
                  <v-alert v-if="!searchResult || searchResult.length === 0" type="info" text color="blue-grey"
                    class="rounded-lg mb-0">
                    Không tìm thấy kết quả phù hợp. Vui lòng kiểm tra lại thông tin.
                  </v-alert>

                  <template v-else>
                    <v-card v-for="(item, index) in searchResult" :key="index" outlined
                      :class="item.status === 'WAITCF' ? 'rounded-lg border mb-3 cursor-pointer hover-card' : 'rounded-lg border mb-3 grey lighten-5'"
                      :style="item.status === 'WAITCF' ? 'border-left: 4px solid #004d40 !important;' : 'opacity: 0.85;'"
                      @click="item.status === 'WAITCF' ? fetchProfile(item.code) : null">
                      <v-card-text>
                        <div class="d-flex justify-space-between mb-2">
                          <span class="font-weight-medium">Mã ứng tuyển:</span>
                          <span class="font-weight-bold text-uppercase d-flex align-center">
                            {{ item.code }}
                            <v-chip v-if="item.status === 'WAITCF'" x-small color="teal" dark
                              class="ml-2 font-weight-bold">
                              <v-icon x-small left>mdi-pencil</v-icon>
                              Được phép sửa
                            </v-chip>
                          </span>
                        </div>
                        <div class="d-flex justify-space-between mb-2">
                          <span class="font-weight-medium">Họ tên:</span>
                          <span class="font-weight-bold text-uppercase">{{ item.name }}</span>
                        </div>
                        <div class="d-flex justify-space-between mb-2">
                          <span class="font-weight-medium">Vị trí ứng tuyển:</span>
                          <span class="teal--text font-weight-bold">{{ item.job }}</span>
                        </div>
                        <div class="d-flex justify-space-between">
                          <span class="font-weight-medium">Trạng thái:</span>
                          <v-chip small :color="getStatusColor(item.status)" text-color="white"
                            class="font-weight-bold px-3">
                            {{ getStatusText(item.status) }}
                          </v-chip>
                        </div>
                      </v-card-text>
                    </v-card>
                  </template>
                </div>
              </v-expand-transition>
            </v-card-text>
            <v-card-actions class="px-6 pb-6 pt-0">
              <v-btn color="teal darken-4" block dark large class="rounded-lg font-weight-bold" :loading="isLoading"
                @click="search" type="button">
                TRA CỨU
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Dialog Thông tin ứng viên -->
      <v-dialog v-model="dialog" max-width="800px" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card :class="$vuetify.breakpoint.xsOnly ? '' : 'rounded-xl'">
          <v-card-title class="teal darken-4 py-4 d-flex justify-space-between align-center"
            style="position: sticky; top: 0; z-index: 10;">
            <span class="text-h6 font-weight-bold mb-0 white--text">THÔNG TIN ỨNG VIÊN</span>
            <v-btn icon dark @click="dialog = false" class="ma-0">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-4 pa-sm-6" :style="$vuetify.breakpoint.xsOnly ? '' : 'max-height: 75vh;'">
            <div v-if="isProfileLoading" class="text-center py-10">
              <v-progress-circular indeterminate color="teal darken-4" size="50"></v-progress-circular>
            </div>

            <v-form v-else-if="profileData" ref="profileForm" @submit.prevent="saveProfile">
              <v-row>
                <!-- Avatar & Name Panel (Top on Mobile, Left on Desktop) -->
                <v-col cols="12" md="4" class="text-center">
                  <v-card outlined class="pa-4 rounded-xl mb-4 text-center border-teal-light"
                    style="background-color: #fafafa; border: 1px solid #B2DFDB !important;">
                    <v-avatar size="120" color="teal lighten-5" class="mb-3 elevation-2">
                      <img v-if="profileData.avatar" :src="profileData.avatar" alt="Avatar">
                      <v-icon v-else size="60" color="teal darken-3">mdi-account</v-icon>
                    </v-avatar>
                    <v-text-field v-model="profileData.name" outlined dense class="font-weight-bold mt-2"
                      hide-details="auto" :rules="[v => !!v || 'Vui lòng nhập họ tên']">
                      <template v-slot:label>
                        Họ tên <span class="red--text">*</span>
                      </template>
                    </v-text-field>
                    <div class="text-subtitle-2 grey--text text--darken-2 mt-2 font-weight-bold">
                      Mã hồ sơ: {{ profileData.code }}
                    </div>
                  </v-card>
                </v-col>

                <!-- Detailed Info Panel (Right on Desktop) -->
                <v-col cols="12" md="8">
                  <!-- Section 1: Thông tin cá nhân -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-account-circle-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Thông tin
                        cá
                        nhân</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-radio-group v-model="profileData.gender" row dense hide-details class="mt-0 mb-2 pl-2">
                          <template v-slot:label>
                            <span class="text-body-2 grey--text text--darken-1 mr-4">Giới tính <span
                                class="red--text">*</span>:</span>
                          </template>
                          <v-radio label="Nam" :value="true" color="teal darken-4"></v-radio>
                          <v-radio label="Nữ" :value="false" color="teal darken-4"></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.doB" type="date" outlined dense hide-details="auto"
                          class="mb-2" :rules="[v => !!v || 'Vui lòng chọn ngày sinh']">
                          <template v-slot:label>
                            Ngày sinh <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-text-field v-model="profileData.phoneNumber" outlined dense hide-details="auto" class="mb-2"
                          :rules="[v => !!v || 'Vui lòng nhập số điện thoại']">
                          <template v-slot:label>
                            Số điện thoại <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 2: Giấy tờ định danh -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-card-account-details-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Giấy tờ
                        định
                        danh</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.idNumber" outlined dense hide-details="auto" class="mb-2"
                          :rules="[v => !!v || 'Vui lòng nhập CCCD/CMND']">
                          <template v-slot:label>
                            CCCD/CMND <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.idDate" type="date" outlined dense hide-details="auto"
                          class="mb-2" :rules="[v => !!v || 'Vui lòng chọn ngày cấp']">
                          <template v-slot:label>
                            Ngày cấp <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <v-autocomplete v-model="profileData.idPlace" :items="idPlaces" outlined dense
                          hide-details="auto" class="mb-2" :rules="[v => !!v || 'Vui lòng chọn nơi cấp']">
                          <template v-slot:label>
                            Nơi cấp <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-file-input v-model="idFrontFile" outlined dense hide-details="auto" class="mb-2"
                          prepend-icon="" append-icon="mdi-camera" accept="image/*" @change="uploadIdFront"
                          :loading="isUploadingIdFront" placeholder="Chọn ảnh mặt trước"
                          :rules="[() => !!profileData.idFrontImageUrl || 'Vui lòng tải lên ảnh mặt trước CCCD']">
                          <template v-slot:label>
                            Mặt trước CCCD <span class="red--text">*</span>
                          </template>
                        </v-file-input>
                        <div v-if="profileData.idFrontImageUrl" class="mt-2">
                          <div class="d-flex align-center justify-space-between py-1 px-2 rounded grey lighten-4 border"
                            style="border-style: dashed !important; border-color: #B2DFDB !important;">
                            <div class="d-flex align-center text-caption text-truncate mr-2"
                              style="max-width: calc(100% - 60px);">
                              <v-icon small color="teal" class="mr-1">
                                {{ isImageFile(profileData.idFrontImageUrl) ? 'mdi-image-outline' :
                                  'mdi-file-document-outline' }}
                              </v-icon>
                              <a :href="getFileLink(profileData.idFrontImageUrl)" target="_blank"
                                class="teal--text text--darken-4 font-weight-medium text-truncate">
                                Xem ảnh mặt trước
                              </a>
                            </div>
                            <div v-if="isImageFile(profileData.idFrontImageUrl)" class="d-flex align-center">
                              <v-hover v-slot="{ hover }">
                                <v-card outlined class="overflow-hidden rounded position-relative"
                                  style="border: 1px solid #B2DFDB !important;" width="50" height="35">
                                  <v-img :src="getFileLink(profileData.idFrontImageUrl)" width="50" height="35" contain
                                    class="white"></v-img>
                                  <v-fade-transition>
                                    <v-overlay v-if="hover" absolute color="#004d40" opacity="0.8">
                                      <v-btn icon x-small dark :href="getFileLink(profileData.idFrontImageUrl)"
                                        target="_blank">
                                        <v-icon size="14">mdi-eye</v-icon>
                                      </v-btn>
                                    </v-overlay>
                                  </v-fade-transition>
                                </v-card>
                              </v-hover>
                            </div>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-file-input v-model="idBackFile" outlined dense hide-details="auto" class="mb-2"
                          prepend-icon="" append-icon="mdi-camera" accept="image/*" @change="uploadIdBack"
                          :loading="isUploadingIdBack" placeholder="Chọn ảnh mặt sau"
                          :rules="[() => !!profileData.idBackImageUrl || 'Vui lòng tải lên ảnh mặt sau CCCD']">
                          <template v-slot:label>
                            Mặt sau CCCD <span class="red--text">*</span>
                          </template>
                        </v-file-input>
                        <div v-if="profileData.idBackImageUrl" class="mt-2">
                          <div class="d-flex align-center justify-space-between py-1 px-2 rounded grey lighten-4 border"
                            style="border-style: dashed !important; border-color: #B2DFDB !important;">
                            <div class="d-flex align-center text-caption text-truncate mr-2"
                              style="max-width: calc(100% - 60px);">
                              <v-icon small color="teal" class="mr-1">
                                {{ isImageFile(profileData.idBackImageUrl) ? 'mdi-image-outline' :
                                  'mdi-file-document-outline'
                                }}
                              </v-icon>
                              <a :href="getFileLink(profileData.idBackImageUrl)" target="_blank"
                                class="teal--text text--darken-4 font-weight-medium text-truncate">
                                Xem ảnh mặt sau
                              </a>
                            </div>
                            <div v-if="isImageFile(profileData.idBackImageUrl)" class="d-flex align-center">
                              <v-hover v-slot="{ hover }">
                                <v-card outlined class="overflow-hidden rounded position-relative"
                                  style="border: 1px solid #B2DFDB !important;" width="50" height="35">
                                  <v-img :src="getFileLink(profileData.idBackImageUrl)" width="50" height="35" contain
                                    class="white"></v-img>
                                  <v-fade-transition>
                                    <v-overlay v-if="hover" absolute color="#004d40" opacity="0.8">
                                      <v-btn icon x-small dark :href="getFileLink(profileData.idBackImageUrl)"
                                        target="_blank">
                                        <v-icon size="14">mdi-eye</v-icon>
                                      </v-btn>
                                    </v-overlay>
                                  </v-fade-transition>
                                </v-card>
                              </v-hover>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 3: Địa chỉ thường trú -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-map-marker-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Địa chỉ
                        thường
                        trú</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.cityId1" :items="newCities" item-text="name"
                          item-value="id" outlined dense hide-details="auto" class="mb-2"
                          :rules="[v => !!v || 'Vui lòng chọn tỉnh/thành']" @change="fetchNewWard(profileData.cityId1)">
                          <template v-slot:label>
                            Tỉnh/Thành <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.wardId1" :items="newWards" item-text="name" item-value="id"
                          outlined dense hide-details="auto" class="mb-2"
                          :rules="[v => !!v || 'Vui lòng chọn phường/xã']">
                          <template v-slot:label>
                            Phường/Xã <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12">
                        <v-text-field v-model="profileData.address1" outlined dense hide-details="auto" class="mb-2"
                          :rules="[v => !!v || 'Vui lòng nhập địa chỉ chi tiết']">
                          <template v-slot:label>
                            Địa chỉ chi tiết <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 4: Trình độ học vấn -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-school-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Trình độ
                        học
                        vấn</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.educationLevelCode" :items="educationLevels"
                          item-text="name" item-value="code" outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => !!v || 'Vui lòng chọn trình độ học vấn']">
                          <template v-slot:label>
                            Trình độ học vấn <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.educationMajorCode" :items="educationMajors"
                          item-text="name" item-value="code" outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => !!v || 'Vui lòng chọn chuyên ngành']">
                          <template v-slot:label>
                            Chuyên ngành <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.universityCode" :items="universities" item-text="name"
                          item-value="code" outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => !!v || 'Vui lòng chọn trường đào tạo']">
                          <template v-slot:label>
                            Cơ sở đào tạo / Trường <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select v-model="profileData.eduRating" :items="['Xuất sắc', 'Giỏi', 'Khá', 'Trung Bình']"
                          outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => !!v || 'Vui lòng chọn xếp loại tốt nghiệp']">
                          <template v-slot:label>
                            Xếp loại tốt nghiệp <span class="red--text">*</span>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select v-model="profileData.graduateStatus" :items="[{text: 'Đã tốt nghiệp', value: true}, {text: 'Chưa tốt nghiệp', value: false}]"
                          outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => v !== undefined && v !== null || 'Vui lòng chọn trạng thái tốt nghiệp']">
                          <template v-slot:label>
                            Trạng thái tốt nghiệp <span class="red--text">*</span>
                          </template>
                        </v-select>
                      </v-col>
                      <div style="width: 100%;"></div>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.eduDateFrom" type="date" outlined dense hide-details="auto"
                          class="mb-2" :rules="[v => !!v || 'Vui lòng chọn ngày bắt đầu']">
                          <template v-slot:label>
                            Học từ ngày <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.eduDateTo" type="date" outlined dense hide-details="auto"
                          class="mb-2" :rules="[v => !!v || 'Vui lòng chọn ngày kết thúc']">
                          <template v-slot:label>
                            Học đến ngày <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 4.2: Trình độ học vấn thứ 2 -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-school-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Trình độ học vấn thứ 2</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.educationLevel2Code" :items="educationLevels"
                          item-text="name" item-value="code" outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Trình độ học vấn thứ 2
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.educationMajor2Code" :items="educationMajors"
                          item-text="name" item-value="code" outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Chuyên ngành thứ 2
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.university2Code" :items="universities" item-text="name"
                          item-value="code" outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Cơ sở đào tạo / Trường thứ 2
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select v-model="profileData.eduRating2" :items="['Xuất sắc', 'Giỏi', 'Khá', 'Trung Bình']"
                          outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Xếp loại tốt nghiệp thứ 2
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-select v-model="profileData.graduateStatus2" :items="[{text: 'Đã tốt nghiệp', value: true}, {text: 'Chưa tốt nghiệp', value: false}]"
                          outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Trạng thái tốt nghiệp (Bằng 2)
                          </template>
                        </v-select>
                      </v-col>
                      <div style="width: 100%;"></div>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.eduDateFrom2" type="date" outlined dense hide-details="auto"
                          class="mb-2">
                          <template v-slot:label>
                            Học từ ngày (Bằng 2)
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.eduDateTo2" type="date" outlined dense hide-details="auto"
                          class="mb-2">
                          <template v-slot:label>
                            Học đến ngày (Bằng 2)
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 4.3: Chứng chỉ hành nghề -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-certificate-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Chứng chỉ hành nghề</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-select v-model="profileData.hasPharmaCert" :items="[{text: 'Có', value: true}, {text: 'Không', value: false}]"
                          outlined dense hide-details="auto" class="mb-2" clearable>
                          <template v-slot:label>
                            Chứng chỉ hành nghề dược
                          </template>
                        </v-select>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 5: Tài khoản ngân hàng & Thông tin thuế/BHXH -->
                  <div class="mb-6">
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-bank-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Tài khoản
                        &
                        Thuế / BHXH</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-autocomplete v-model="profileData.bankCode" :items="banks" item-text="name" item-value="code"
                          outlined dense hide-details="auto" class="mb-2" clearable
                          :rules="[v => !!v || 'Vui lòng chọn ngân hàng']">
                          <template v-slot:label>
                            Ngân hàng <span class="red--text">*</span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.bankNumber" outlined dense hide-details="auto" class="mb-2"
                          clearable :rules="[v => !!v || 'Vui lòng nhập số tài khoản']">
                          <template v-slot:label>
                            Số tài khoản <span class="red--text">*</span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.taxNumber" label="Mã số thuế" outlined dense
                          hide-details="auto" class="mb-2" clearable></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field v-model="profileData.insuranceNumber" label="Số sổ BHXH" outlined dense
                          hide-details="auto" class="mb-2" clearable></v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <!-- Section 6: Hồ sơ đính kèm -->
                  <div>
                    <div class="d-flex align-center mb-3">
                      <v-icon color="teal darken-4" class="mr-2" small>mdi-file-document-outline</v-icon>
                      <span class="font-weight-bold text-subtitle-2 teal--text text--darken-4 uppercase-title">Hồ sơ
                        đính
                        kèm</span>
                      <v-divider class="ml-3"></v-divider>
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-file-input v-model="attachmentFile" outlined dense hide-details="auto" class="mb-2"
                          prepend-icon="" append-icon="mdi-paperclip" @change="uploadAttachment"
                          :loading="isUploadingAttachment" placeholder="Chọn file đính kèm"
                          :rules="[() => !!profileData.attachmentUrl || 'Vui lòng tải lên giấy khám sức khỏe']">
                          <template v-slot:label>
                            Giấy khám sức khỏe <span class="red--text">*</span>
                          </template>
                        </v-file-input>
                        <div v-if="profileData.attachmentUrl" class="mt-2">
                          <div class="d-flex align-center justify-space-between py-1 px-2 rounded grey lighten-4 border"
                            style="border-style: dashed !important; border-color: #B2DFDB !important;">
                            <div class="d-flex align-center text-caption text-truncate mr-2"
                              style="max-width: calc(100% - 60px);">
                              <v-icon small color="teal" class="mr-1">
                                {{ isImageFile(profileData.attachmentUrl) ? 'mdi-image-outline' :
                                  'mdi-file-document-outline'
                                }}
                              </v-icon>
                              <a :href="getFileLink(profileData.attachmentUrl)" target="_blank"
                                class="teal--text text--darken-4 font-weight-medium text-truncate">
                                Xem file đính kèm khác
                              </a>
                            </div>
                            <div v-if="isImageFile(profileData.attachmentUrl)" class="d-flex align-center">
                              <v-hover v-slot="{ hover }">
                                <v-card outlined class="overflow-hidden rounded position-relative"
                                  style="border: 1px solid #B2DFDB !important;" width="50" height="35">
                                  <v-img :src="getFileLink(profileData.attachmentUrl)" width="50" height="35" contain
                                    class="white"></v-img>
                                  <v-fade-transition>
                                    <v-overlay v-if="hover" absolute color="#004d40" opacity="0.8">
                                      <v-btn icon x-small dark :href="getFileLink(profileData.attachmentUrl)"
                                        target="_blank">
                                        <v-icon size="14">mdi-eye</v-icon>
                                      </v-btn>
                                    </v-overlay>
                                  </v-fade-transition>
                                </v-card>
                              </v-hover>
                            </div>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-file-input v-model="eduFile" outlined dense hide-details="auto" class="mb-2" prepend-icon=""
                          append-icon="mdi-paperclip" @change="uploadEduFile" :loading="isUploadingEduFile"
                          placeholder="Chọn file bằng cấp"
                          :rules="[() => !!profileData.eduFileUrl || 'Vui lòng tải lên bằng cấp']">
                          <template v-slot:label>
                            Tải lên Bằng cấp <span class="red--text">*</span>
                          </template>
                        </v-file-input>
                        <div v-if="profileData.eduFileUrl" class="mt-2">
                          <div class="d-flex align-center justify-space-between py-1 px-2 rounded grey lighten-4 border"
                            style="border-style: dashed !important; border-color: #B2DFDB !important;">
                            <div class="d-flex align-center text-caption text-truncate mr-2"
                              style="max-width: calc(100% - 60px);">
                              <v-icon small color="teal" class="mr-1">
                                {{ isImageFile(profileData.eduFileUrl) ? 'mdi-image-outline' :
                                  'mdi-file-document-outline' }}
                              </v-icon>
                              <a :href="getFileLink(profileData.eduFileUrl)" target="_blank"
                                class="teal--text text--darken-4 font-weight-medium text-truncate">
                                Xem bằng cấp đã tải
                              </a>
                            </div>
                            <div v-if="isImageFile(profileData.eduFileUrl)" class="d-flex align-center">
                              <v-hover v-slot="{ hover }">
                                <v-card outlined class="overflow-hidden rounded position-relative"
                                  style="border: 1px solid #B2DFDB !important;" width="50" height="35">
                                  <v-img :src="getFileLink(profileData.eduFileUrl)" width="50" height="35" contain
                                    class="white"></v-img>
                                  <v-fade-transition>
                                    <v-overlay v-if="hover" absolute color="#004d40" opacity="0.8">
                                      <v-btn icon x-small dark :href="getFileLink(profileData.eduFileUrl)"
                                        target="_blank">
                                        <v-icon size="14">mdi-eye</v-icon>
                                      </v-btn>
                                    </v-overlay>
                                  </v-fade-transition>
                                </v-card>
                              </v-hover>
                            </div>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-file-input v-model="eduFile2" outlined dense hide-details="auto" class="mb-2" prepend-icon=""
                          append-icon="mdi-paperclip" @change="uploadEduFile2" :loading="isUploadingEduFile2"
                          placeholder="Chọn file bằng cấp thứ 2">
                          <template v-slot:label>
                            Tải lên Bằng cấp thứ 2
                          </template>
                        </v-file-input>
                        <div v-if="profileData.eduFile2Url" class="mt-2">
                          <div class="d-flex align-center justify-space-between py-1 px-2 rounded grey lighten-4 border"
                            style="border-style: dashed !important; border-color: #B2DFDB !important;">
                            <div class="d-flex align-center text-caption text-truncate mr-2"
                              style="max-width: calc(100% - 60px);">
                              <v-icon small color="teal" class="mr-1">
                                {{ isImageFile(profileData.eduFile2Url) ? 'mdi-image-outline' :
                                  'mdi-file-document-outline' }}
                              </v-icon>
                              <a :href="getFileLink(profileData.eduFile2Url)" target="_blank"
                                class="teal--text text--darken-4 font-weight-medium text-truncate">
                                Xem bằng cấp 2 đã tải
                              </a>
                            </div>
                            <div v-if="isImageFile(profileData.eduFile2Url)" class="d-flex align-center">
                              <v-hover v-slot="{ hover }">
                                <v-card outlined class="overflow-hidden rounded position-relative"
                                  style="border: 1px solid #B2DFDB !important;" width="50" height="35">
                                  <v-img :src="getFileLink(profileData.eduFile2Url)" width="50" height="35" contain
                                    class="white"></v-img>
                                  <v-fade-transition>
                                    <v-overlay v-if="hover" absolute color="#004d40" opacity="0.8">
                                      <v-btn icon x-small dark :href="getFileLink(profileData.eduFile2Url)"
                                        target="_blank">
                                        <v-icon size="14">mdi-eye</v-icon>
                                      </v-btn>
                                    </v-overlay>
                                  </v-fade-transition>
                                </v-card>
                              </v-hover>
                            </div>
                          </div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4 justify-end" v-if="!isProfileLoading && profileData">
            <v-btn text color="grey darken-1" class="font-weight-bold mr-2" @click="dialog = false">HỦY</v-btn>
            <v-btn color="teal darken-4" dark class="font-weight-bold px-6 rounded-lg" :loading="isSavingProfile"
              @click="saveProfile">LƯU THAY ĐỔI</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import http from "@/components/services/http-common";

export default {
  name: 'JobSearch',
  data() {
    return {
      valid: true,
      searchQuery: '',
      isLoading: false,
      hasSearched: false,
      searchResult: null,
      dialog: false,
      isProfileLoading: false,
      isSavingProfile: false,
      profileData: null,
      newCities: [],
      newWards: [],
      educationLevels: [],
      educationMajors: [],
      universities: [],
      banks: [],
      attachmentFile: null,
      eduFile: null,
      eduFile2: null,
      idFrontFile: null,
      idBackFile: null,
      isUploadingAttachment: false,
      isUploadingEduFile: false,
      isUploadingEduFile2: false,
      isUploadingIdFront: false,
      isUploadingIdBack: false,
    };
  },
  computed: {
    idPlaces() {
      const fixed = ["Cục Cảnh sát ĐKQL cư trú và DLQG về dân cư", "Cục Cảnh sát quản lý hành chính về trật tự xã hội"];
      if (!this.newCities) return fixed;
      const cityData = this.newCities.map(x => x.name);
      return fixed.concat(cityData);
    }
  },
  mounted() {
    this.fetchNewCity();
    this.fetchEducationLevels();
    this.fetchEducationMajors();
    this.fetchUniversities();
    this.fetchBanks();
  },
  methods: {
    search() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        this.hasSearched = true;

        http.get('candidates/job-search', { params: { inputSearch: this.searchQuery } })
          .then(res => {
            let data = res.data.data || res.data;
            if (Array.isArray(data) && data.length > 0) {
              let mappedData = data.map(item => ({
                code: item.code,
                name: item.name || item.Name || item.hoTen,
                job: item.job || item.Job || item.JobName || item.jobName || item.viTriUngTuyen,
                status: item.status || item.Status || item.trangThai,
                rawDate: item.createdAt || item.CreatedAt || item.createdDate || item.CreatedDate || item.ngayNop || item.ngayTao || null,
                id: item.id || item.Id || 0
              }));

              mappedData.sort((a, b) => {
                const acceptStatuses = ['Trúng tuyển', 'Accept', 'Đã tiếp nhận', 'Đạt'];
                const aIsAccept = acceptStatuses.includes(a.status);
                const bIsAccept = acceptStatuses.includes(b.status);

                if (aIsAccept && !bIsAccept) return -1;
                if (!aIsAccept && bIsAccept) return 1;

                if (a.rawDate && b.rawDate) {
                  return new Date(b.rawDate) - new Date(a.rawDate);
                }
                if (a.id && b.id) {
                  return b.id - a.id;
                }
                return 0;
              });

              this.searchResult = mappedData;
            } else {
              this.searchResult = [];
            }
          })
          .catch(err => {
            console.error(err);
            this.searchResult = null;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    fetchNewCity() {
      http.get("addresses/cities")
        .then(res => {
          this.newCities = res.data.data;
        })
        .catch(err => console.error(err));
    },
    fetchNewWard(val) {
      if (!val) {
        this.newWards = [];
        return;
      }
      http.get('addresses/wards?cityId=' + val)
        .then(res => {
          this.newWards = res.data.data;
        })
        .catch(err => console.error(err));
    },
    fetchEducationLevels() {
      http.get('educationLevels/fetch')
        .then(res => {
          this.educationLevels = res.data.data || res.data || [];
        })
        .catch(err => console.error(err));
    },
    fetchEducationMajors() {
      http.get('educationMajors/fetch')
        .then(res => {
          this.educationMajors = res.data.data || res.data || [];
        })
        .catch(err => console.error(err));
    },
    fetchUniversities() {
      http.get('universities/fetch')
        .then(res => {
          this.universities = res.data.data || res.data || [];
        })
        .catch(err => console.error(err));
    },
    fetchBanks() {
      http.get('banks')
        .then(res => {
          this.banks = res.data.data || res.data || [];
        })
        .catch(err => console.error(err));
    },
    uploadAttachment(file) {
      if (!file) return;
      this.isUploadingAttachment = true;
      const form = new FormData();
      form.append("file", file);

      http.post("NasUploads?folder=Candidates", form)
        .then(res => {
          let response = res.data;
          let url = response.data || response.path || '';
          this.$set(this.profileData, 'attachmentUrl', url);
        })
        .catch(err => {
          console.error("Upload attachment failed:", err);
          alert("Tải file thất bại!");
        })
        .finally(() => {
          this.isUploadingAttachment = false;
        });
    },
    uploadEduFile(file) {
      if (!file) return;
      this.isUploadingEduFile = true;
      const form = new FormData();
      form.append("file", file);

      http.post("NasUploads?folder=Candidates", form)
        .then(res => {
          let response = res.data;
          let url = response.data || response.path || '';
          this.$set(this.profileData, 'eduFileUrl', url);
        })
        .catch(err => {
          console.error("Upload edu file failed:", err);
          alert("Tải file thất bại!");
        })
        .finally(() => {
          this.isUploadingEduFile = false;
        });
    },
    uploadEduFile2(file) {
      if (!file) return;
      this.isUploadingEduFile2 = true;
      const form = new FormData();
      form.append("file", file);

      http.post("NasUploads?folder=Candidates", form)
        .then(res => {
          let response = res.data;
          let url = response.data || response.path || '';
          this.$set(this.profileData, 'eduFile2Url', url);
        })
        .catch(err => {
          console.error("Upload second edu file failed:", err);
          alert("Tải file thất bại!");
        })
        .finally(() => {
          this.isUploadingEduFile2 = false;
        });
    },
    uploadIdFront(file) {
      if (!file) return;
      this.isUploadingIdFront = true;
      const form = new FormData();
      form.append("file", file);

      http.post("NasUploads?folder=Candidates", form)
        .then(res => {
          let response = res.data;
          let url = response.data || response.path || '';
          this.$set(this.profileData, 'idFrontImageUrl', url);
        })
        .catch(err => {
          console.error("Upload front ID failed:", err);
          alert("Tải file thất bại!");
        })
        .finally(() => {
          this.isUploadingIdFront = false;
        });
    },
    uploadIdBack(file) {
      if (!file) return;
      this.isUploadingIdBack = true;
      const form = new FormData();
      form.append("file", file);

      http.post("NasUploads?folder=Candidates", form)
        .then(res => {
          let response = res.data;
          let url = response.data || response.path || '';
          this.$set(this.profileData, 'idBackImageUrl', url);
        })
        .catch(err => {
          console.error("Upload back ID failed:", err);
          alert("Tải file thất bại!");
        })
        .finally(() => {
          this.isUploadingIdBack = false;
        });
    },
    getFileLink(url) {
      if (!url) return '';
      if (url.startsWith('http://') || url.startsWith('https://')) return url;
      const cleanedUrl = url.replace(/^\/?(wwwroot\/)?/, '');
      return process.env.VUE_APP_BASE_URL + cleanedUrl;
    },
    isImageFile(url) {
      if (!url) return false;
      const cleanUrl = url.split('?')[0].split('#')[0];
      const extension = cleanUrl.substring(cleanUrl.lastIndexOf('.') + 1).toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
    },
    getStatusText(status) {
      const statusMap = {
        'NEW': 'Mới',
        'PEND': 'Chờ',
        'WAITCF': 'Chờ xác nhận',
        'PASS': 'Đạt',
        'FAIL': 'Loại',
        'NMEET': 'Không phỏng vấn',
        'BLACK': 'Không liên hệ',
        'ACCEPT': 'Nhận việc'
      };
      return statusMap[status] || status;
    },
    getStatusColor(status) {
      const colorMap = {
        'NEW': 'info',
        'PEND': 'warning',
        'WAITCF': 'orange darken-1',
        'PASS': 'success',
        'FAIL': 'error',
        'ACCEPT': 'teal darken-1',
        'NMEET': 'blue-grey lighten-1',
        'BLACK': 'grey darken-2',

        // Vietnamese string fallbacks
        'Mới': 'info',
        'Phỏng vấn': 'warning',
        'Chờ': 'warning',
        'Chờ xác nhận': 'orange darken-1',
        'Đạt': 'success',
        'Trúng tuyển': 'success',
        'Loại': 'error',
        'Từ chối': 'error',
        'Không phỏng vấn': 'blue-grey lighten-1',
        'Không liên hệ': 'grey darken-2',
        'Duyệt': 'success',
        'Nhận việc': 'teal darken-1',
        'Đang xử lý': 'warning'
      };
      return colorMap[status] || 'grey';
    },
    fetchProfile(code) {
      if (!code) return;
      if (this.searchResult) {
        const item = this.searchResult.find(x => x.code === code);
        if (item && item.status !== 'WAITCF') {
          return;
        }
      }
      this.dialog = true;
      this.isProfileLoading = true;
      this.profileData = null;
      this.attachmentFile = null;
      this.eduFile = null;
      this.eduFile2 = null;
      this.idFrontFile = null;
      this.idBackFile = null;

      http.get(`candidates/profile/${code}`)
        .then(res => {
          let data = res.data.data || res.data;
          this.profileData = {
            code: data.code || data.Code,
            avatar: data.avatar || data.Avatar,
            name: data.name || data.Name,
            gender: data.gender !== undefined ? data.gender : data.Gender,
            doB: data.doB || data.DoB,
            phoneNumber: data.phoneNumber || data.PhoneNumber,
            idNumber: data.idNumber || data.IdNumber,
            idDate: data.idDate || data.IdDate,
            idPlace: data.idPlace || data.IdPlace,
            cityId1: data.cityId1 || data.CityId1,
            wardId1: data.wardId1 || data.WardId1,
            address1: data.address1 || data.Address1,
            educationLevelCode: data.educationLevelCode || data.EducationLevelCode || null,
            educationMajorCode: data.educationMajorCode || data.EducationMajorCode || null,
            universityCode: data.universityCode || data.UniversityCode || null,
            educationLevel2Code: data.educationLevel2Code || data.EducationLevel2Code || null,
            educationMajor2Code: data.educationMajor2Code || data.EducationMajor2Code || null,
            university2Code: data.university2Code || data.University2Code || null,
            attachmentUrl: data.attachmentUrl || data.AttachmentUrl || null,
            eduFileUrl: data.eduFileUrl || data.EduFileUrl || null,
            eduFile2Url: data.eduFile2Url || data.EduFile2Url || null,
            idFrontImageUrl: data.idFrontImageUrl || data.IdFrontImageUrl || null,
            idBackImageUrl: data.idBackImageUrl || data.IdBackImageUrl || null,
            eduRating: data.eduRating || data.EduRating || null,
            eduRating2: data.eduRating2 || data.EduRating2 || null,
            eduDateFrom: data.eduDateFrom || data.EduDateFrom || null,
            eduDateTo: data.eduDateTo || data.EduDateTo || null,
            eduDateFrom2: data.eduDateFrom2 || data.EduDateFrom2 || null,
            eduDateTo2: data.eduDateTo2 || data.EduDateTo2 || null,
            graduateStatus: data.graduateStatus === true || data.graduateStatus === 1 ? true : (data.graduateStatus === false || data.graduateStatus === 0 ? false : null),
            graduateStatus2: data.graduateStatus2 === true || data.graduateStatus2 === 1 ? true : (data.graduateStatus2 === false || data.graduateStatus2 === 0 ? false : null),
            hasPharmaCert: data.hasPharmaCert === true || data.hasPharmaCert === 1 || data.pharmaCert === true || data.pharmaCert === 1 || data.pharmaCert === 'CO' ? true : (data.hasPharmaCert === false || data.hasPharmaCert === 0 || data.pharmaCert === false || data.pharmaCert === 0 || data.pharmaCert === 'KHONG' ? false : null),
            bankCode: data.bankCode || data.BankCode || null,
            bankNumber: data.bankNumber || data.BankNumber || null,
            taxNumber: data.taxNumber || data.TaxNumber || null,
            insuranceNumber: data.insuranceNumber || data.InsuranceNumber || null,
          };

          if (this.profileData.cityId1) {
            this.fetchNewWard(this.profileData.cityId1);
          }

          if (this.profileData.doB) {
            this.profileData.doB = this.formatDateForInput(this.profileData.doB);
          }
          if (this.profileData.idDate) {
            this.profileData.idDate = this.formatDateForInput(this.profileData.idDate);
          }
          if (this.profileData.eduDateFrom) {
            this.profileData.eduDateFrom = this.formatDateForInput(this.profileData.eduDateFrom);
          }
          if (this.profileData.eduDateTo) {
            this.profileData.eduDateTo = this.formatDateForInput(this.profileData.eduDateTo);
          }
          if (this.profileData.eduDateFrom2) {
            this.profileData.eduDateFrom2 = this.formatDateForInput(this.profileData.eduDateFrom2);
          }
          if (this.profileData.eduDateTo2) {
            this.profileData.eduDateTo2 = this.formatDateForInput(this.profileData.eduDateTo2);
          }
        })
        .catch(err => {
          console.error('Error fetching profile:', err);
        })
        .finally(() => {
          this.isProfileLoading = false;
        });
    },
    saveProfile() {
      if (!this.profileData) return;
      if (this.$refs.profileForm && !this.$refs.profileForm.validate()) {
        return;
      }
      this.isSavingProfile = true;

      let payload = {
        Code: this.profileData.code,
        Avatar: this.profileData.avatar,
        Name: this.profileData.name,
        Gender: this.profileData.gender,
        DoB: this.profileData.doB,
        PhoneNumber: this.profileData.phoneNumber,
        IdNumber: this.profileData.idNumber,
        IdDate: this.profileData.idDate,
        IdPlace: this.profileData.idPlace,
        CityId1: Number(this.profileData.cityId1) || 0,
        WardId1: Number(this.profileData.wardId1) || 0,
        Address1: this.profileData.address1,
        EducationLevelCode: this.profileData.educationLevelCode,
        EducationMajorCode: this.profileData.educationMajorCode,
        UniversityCode: this.profileData.universityCode,
        EducationLevel2Code: this.profileData.educationLevel2Code,
        EducationMajor2Code: this.profileData.educationMajor2Code,
        University2Code: this.profileData.university2Code,
        AttachmentUrl: this.profileData.attachmentUrl,
        EduFileUrl: this.profileData.eduFileUrl,
        EduFile2Url: this.profileData.eduFile2Url,
        IdFrontImageUrl: this.profileData.idFrontImageUrl,
        IdBackImageUrl: this.profileData.idBackImageUrl,
        EduRating: this.profileData.eduRating,
        EduRating2: this.profileData.eduRating2,
        EduDateFrom: this.profileData.eduDateFrom,
        EduDateTo: this.profileData.eduDateTo,
        EduDateFrom2: this.profileData.eduDateFrom2,
        EduDateTo2: this.profileData.eduDateTo2,
        GraduateStatus: this.profileData.graduateStatus,
        GraduateStatus2: this.profileData.graduateStatus2,
        HasPharmaCert: this.profileData.hasPharmaCert,
        PharmaCert: this.profileData.hasPharmaCert === true ? "CO" : (this.profileData.hasPharmaCert === false ? "KHONG" : null),
        BankCode: this.profileData.bankCode,
        BankNumber: this.profileData.bankNumber,
        TaxNumber: this.profileData.taxNumber,
        InsuranceNumber: this.profileData.insuranceNumber
      };

      http.put(`candidates/profile/${this.profileData.code}`, payload)
        .then(res => {
          // Success
          this.dialog = false;
          // Optionally trigger a re-fetch of the search here if needed
          // this.search();
        })
        .catch(err => {
          console.error('Error saving profile:', err);
        })
        .finally(() => {
          this.isSavingProfile = false;
        });
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
  }
};
</script>

<style scoped>
.border {
  border: 1px solid #e0e0e0;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-card:hover {
  background-color: #f0fdfa !important;
  border-color: #004d40 !important;
  transition: all 0.3s ease;
}

.uppercase-title {
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-size: 0.85rem !important;
}

.border-teal-light {
  border: 1px solid #B2DFDB !important;
}
</style>
